import { Navigate, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import { useAuth } from "@/lib/auth-context";
import { AuthShell } from "@/features/auth/AuthShell";

/**
 * RequireAuth — route guard. Redirects unauthenticated users to /signin and
 * pending accounts to /pending. Suspended accounts get a notice.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const loc = useLocation();

  if (status === "loading") return <LoadingShell />;
  if (status === "signed-out") return <Navigate to="/signin" replace state={{ from: loc }} />;
  if (status === "pending") return <Navigate to="/pending" replace />;
  if (status === "suspended") {
    return (
      <AuthShell title="ACCESS SUSPENDED" subtitle="Account suspended">
        <p className="auth-copy">
          This operator account has been suspended. Contact your desk administrator.
        </p>
      </AuthShell>
    );
  }
  return <>{children}</>;
}

/** Keeps signed-in users off /signin: active → ops; pending/suspended → their gate (a fresh
 *  signup landing back here would otherwise sit on the signup form forever — the gate screen
 *  is the one that says "confirm your email" / "trial over"). */
export function RedirectIfAuthed({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  if (status === "loading") return <LoadingShell />;
  if (status === "active") return <Navigate to="/ops" replace />;
  if (status === "pending" || status === "suspended") return <Navigate to="/pending" replace />;
  return <>{children}</>;
}

/** /pending is only meaningful for a signed-in, not-yet-active user: active users advance to
 *  the desk (this is what makes the screen's own 15s polling actually GO somewhere), signed-out
 *  visitors belong on /signin. */
export function PendingGate({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  if (status === "loading") return <LoadingShell />;
  if (status === "signed-out") return <Navigate to="/signin" replace />;
  if (status === "active") return <Navigate to="/ops" replace />;
  return <>{children}</>;
}

export function LoadingShell() {
  return (
    <AuthShell title="INITIALIZING" subtitle="Establishing session">
      <div className="pending-ring">
        <svg viewBox="0 0 120 120" aria-hidden="true">
          <circle className="bk" cx="60" cy="60" r="50" />
          <circle className="arc" cx="60" cy="60" r="50" />
        </svg>
      </div>
    </AuthShell>
  );
}
